/**
 * ⭐⭐ **O TÓPICO OUTROS ATIVOS NO DESENHO DE IMÓVEIS — 18/09/2026.**
 *
 * O segundo da fila do pedido dele (*"o layout e design de IMÓVEIS ser a
 * referência… estou me referindo a parte visual"*; PRD §46): resumo no alto,
 * uma parte por vez, o botão de lançar, a faixa do que foi pesquisado para a
 * parte, uma linha por bem que abre o detalhe.
 *
 * ⛔ **Muda aparência e lugar, nunca o que o botão faz.** `FichaDeOutroAtivo`,
 * `FormularioDeOnus`, o aceite, corrigir, apagar e o registro da pesquisa de
 * marcas (`RegistroDePesquisa`, a mesma peça de Imóveis) são os de antes.
 *
 * ⚠️ **As quatro espécies continuam ditas mesmo vazias** — viraram as marcas da
 * faixa, como as de Veículos. ⚠️ **"Registrei esta pesquisa" de marcas mudou de
 * lugar:** saiu do bloco solto abaixo do tópico e veio para a faixa da parte,
 * como a de Imóveis em 16/09 — e o bloco de baixo deixou de existir, para o
 * mesmo registro não morar em dois lugares.
 */
import { useEffect, useState } from 'react';
import {
  apiInvestigados,
  apiPesquisasRegistradas,
  QUALIFICACOES,
  type AtivoDoInvestigado,
  type EspecieDeOutroAtivo,
  type GravamesDoTopico,
  type Investigado,
  type PesquisaRegistrada,
} from '../lib/api';
import { usarEhMaster } from '../lib/papel';
import { avisar, avisarErro } from './Avisos';
import { Confirmar } from './Confirmar';
import { DOCUMENTADA, FormularioDeOnus, ROTULO, ROTULO_DA_MODALIDADE, dinheiro } from './AtivosLocalizados';
import { ExteriorDaParte, SeloDoExterior, usarExterior } from './ExteriorDaParte';
import { FichaDeOutroAtivo, FONTE_DO_OUTRO, OUTROS, SITUACOES_DO_OUTRO, type SugestaoDeOutroAtivo } from './FichaDeOutroAtivo';
import { iniciais, RegistroDePesquisa, Traco, type PesquisaDoTopico } from './ImoveisDoCaso';
import '../estilos/imoveis-2026.css';

const PESQUISAS_DOS_OUTROS: PesquisaDoTopico[] = [{ servico: 'marcas', rotulo: 'Marcas por titular', achou: 'Achou marca' }];

const emData = (d: string | null | undefined) => (d ? d.split('-').reverse().join('/') : '');
const ativoGravame = (o: { situacao: string | null }) => (o.situacao ?? '').trim().toUpperCase() === 'ATIVO';
const situacao = (a: AtivoDoInvestigado) => (a.outro ? SITUACOES_DO_OUTRO[a.outro].find((s) => s.chave === a.classificacao) : undefined);
const nomeDe = (o: EspecieDeOutroAtivo | null) => OUTROS.find((x) => x.chave === o)?.um ?? '';

type Dados = { ativos: AtivoDoInvestigado[]; gravames: GravamesDoTopico | null };

export function OutrosDoCaso({
  casoId,
  chave,
  aoMudarPesquisa,
}: {
  casoId: string;
  chave: string;
  /** Avisa a página para recarregar a ressalva do capítulo. */
  aoMudarPesquisa?: () => void;
}) {
  const ehMaster = usarEhMaster();
  const [partes, setPartes] = useState<Investigado[] | null>(null);
  const [porParte, setPorParte] = useState<Record<string, Dados>>({});
  const [registradas, setRegistradas] = useState<PesquisaRegistrada[]>([]);
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [revisao, setRevisao] = useState(0);
  const [painel, setPainel] = useState<null | 'registro' | 'exterior'>(null);
  /* ⭐ 28/09/2026 — a busca no exterior. ⚠️ Antes dos retornos antecipados:
     é gancho do React, e gancho não pode depender de a tela já ter partes. */
  const [revisaoDoExterior, setRevisaoDoExterior] = useState(0);
  const exterior = usarExterior(escolhida, revisaoDoExterior);
  const [ficha, setFicha] = useState<null | { corrigindo: AtivoDoInvestigado | null; sugestao?: SugestaoDeOutroAtivo }>(null);

  useEffect(() => {
    let vivo = true;
    void Promise.all([apiInvestigados.listar(casoId), apiPesquisasRegistradas.doCaso(casoId)])
      .then(([i, r]) => {
        if (!vivo) return;
        setPartes(i.investigados);
        setRegistradas(r.registradas);
        setEscolhida((atual) => atual ?? i.investigados[0]?.id ?? null);
      })
      .catch(() => vivo && setPartes([]));
    return () => {
      vivo = false;
    };
  }, [casoId, chave]);

  useEffect(() => {
    if (!partes) return;
    let vivo = true;
    void Promise.all(
      partes.map((p) =>
        apiInvestigados
          .ativos(p.id)
          .then((r) => [p.id, { ativos: r.ativos.filter((a) => a.categoria === 'outros'), gravames: r.porCategoria?.outros ?? null }] as const)
          .catch(() => [p.id, { ativos: [], gravames: null }] as const),
      ),
    ).then((pares) => {
      if (vivo) setPorParte(Object.fromEntries(pares));
    });
    return () => {
      vivo = false;
    };
  }, [partes, revisao]);

  if (partes === null) return <p className="ajuda">Carregando…</p>;
  if (partes.length === 0) {
    return (
      <p className="ajuda ajuda-solta">
        Este caso ainda não tem parte investigada cadastrada. Os ativos se lançam por pessoa — cadastre os investigados
        no caso primeiro.
      </p>
    );
  }

  const parte = partes.find((p) => p.id === escolhida) ?? partes[0]!;
  const dados = porParte[parte.id];
  const todos = Object.values(porParte).flatMap((d) => d.ativos);
  const aoMudar = () => setRevisao((n) => n + 1);
  const marcas = registradas.find((x) => x.investigadoId === parte.id && x.servico === 'marcas') ?? null;

  return (
    <div className="imv">
      <Resumo ativos={todos} />

      <div className="imv-pessoas" role="tablist" aria-label="Parte investigada">
        {partes.map((p) => {
          const n = porParte[p.id]?.ativos.length ?? 0;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={p.id === parte.id}
              className={p.id === parte.id ? 'ativo' : undefined}
              onClick={() => {
                if (ficha) {
                  avisar('Lance ou cancele o formulário aberto antes de trocar de parte.');
                  return;
                }
                setEscolhida(p.id);
                setPainel(null);
              }}
            >
              <span className="imv-av">{iniciais(p.nome)}</span>
              <span className="imv-txt">
                <b>{p.nome}</b>
                <small>{QUALIFICACOES[p.qualificacao]}</small>
              </span>
              <span className={`imv-conta${n === 0 ? ' zero' : ''}`} title={n === 0 ? 'nada lançado' : undefined}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {ficha ? (
        <FichaDeOutroAtivo
          key={ficha.corrigindo?.id ?? 'novo'}
          investigado={parte}
          corrigindo={ficha.corrigindo}
          sugestao={ficha.sugestao ?? null}
          aoFechar={() => setFicha(null)}
          aoGravar={() => {
            setFicha(null);
            aoMudar();
          }}
        />
      ) : (
        <>
          <div className="imv-acoes">
            <button
              type="button"
              className="imv-bt primario"
              onClick={() => {
                setPainel(null);
                setFicha({ corrigindo: null });
              }}
            >
              <Traco d={['M12 5v14', 'M5 12h14']} />
              Lançar outro ativo
            </button>
          </div>

          <div className="imv-fontes">
            <span className="imv-rot">Pesquisado para esta parte</span>
            <span className={`imv-fonte${marcas ? ' ok' : ' falta'}`} title={FONTE_DO_OUTRO.marca}>
              <i />
              Marcas por titular ·{' '}
              {marcas
                ? `${marcas.achou ? 'achou marca' : 'nada consta'} · ${new Date(marcas.em).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}`
                : 'não registrada'}
            </span>
            {(['acao', 'patente', 'direito_minerario'] as const).map((o) => (
              <span key={o} className="imv-fonte falta" title={FONTE_DO_OUTRO[o]}>
                <i />
                {OUTROS.find((x) => x.chave === o)!.varios} · sem caminho no Hub
              </span>
            ))}
            <SeloDoExterior dados={exterior} />
            <span className="imv-esp" />
            <button
              type="button"
              className={`imv-bt fantasma p${painel === 'exterior' ? ' aberto' : ''}`}
              aria-expanded={painel === 'exterior'}
              onClick={() => setPainel(painel === 'exterior' ? null : 'exterior')}
            >
              <Traco d={['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18', 'M3 12h18', 'M12 3a14 14 0 0 1 0 18', 'M12 3a14 14 0 0 0 0 18']} />
              No exterior
            </button>
            <button
              type="button"
              className={`imv-bt fantasma p${painel === 'registro' ? ' aberto' : ''}`}
              aria-expanded={painel === 'registro'}
              onClick={() => setPainel(painel === 'registro' ? null : 'registro')}
            >
              <Traco d={['M5 4h14v16H5z', 'M8.5 8.5h7', 'M8.5 12h7', 'm8.5 15.5 1.5 1.5 3-3']} />
              Registrei esta pesquisa
            </button>
          </div>

          {painel === 'exterior' && (
            <ExteriorDaParte
              investigado={parte}
              dados={exterior}
              ehMaster={ehMaster}
              aoMudar={() => setRevisaoDoExterior((n) => n + 1)}
              aoLancar={(sugestao) => {
                setPainel(null);
                setFicha({ corrigindo: null, sugestao });
              }}
            />
          )}

          {painel === 'registro' && (
            <RegistroDePesquisa
              investigado={parte}
              registradas={registradas}
              ehMaster={ehMaster}
              pesquisas={PESQUISAS_DOS_OUTROS}
              aoMudar={(novas) => {
                setRegistradas(novas);
                aoMudarPesquisa?.();
              }}
            />
          )}

          <ListaDosOutros
            dados={dados}
            investigadoId={parte.id}
            ehMaster={ehMaster}
            aoMudar={aoMudar}
            aoCorrigir={(a) => {
              setPainel(null);
              setFicha({ corrigindo: a });
            }}
          />
        </>
      )}
    </div>
  );
}

/* ── O resumo do caso ─────────────────────────────────────────────────────── */
function Resumo({ ativos }: { ativos: AtivoDoInvestigado[] }) {
  const soma = (lista: AtivoDoInvestigado[]) => {
    const com = lista.filter((a) => a.valorAtribuivel !== null);
    return com.length === 0 ? null : com.reduce((s, a) => s + (a.valorAtribuivel ?? 0), 0);
  };
  const emNome = ativos.filter((a) => a.classificacao === 'acao_em_nome');
  const oneradas = ativos.filter((a) => a.classificacao === 'acao_onerada' || a.classificacao === 'acao_com_constricao');
  const porRegra = ativos.filter((a) => a.foraDaContaPorRegra).length;
  const saiu = ativos.filter((a) => !a.entraNaConsolidacao && !a.foraDaContaPorRegra).length;
  const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;
  const somaEmNome = soma(emNome);
  const somaOneradas = soma(oneradas);
  /* ⭐ 07/10/2026: o direito creditório LIVRE soma (decisão dele) — cartão próprio, só no caso que tem crédito. */
  const temCredito = ativos.some((a) => a.outro === 'direito_creditorio');
  const creditosLivres = ativos.filter((a) => a.classificacao === 'credito_livre');
  const somaCreditos = soma(creditosLivres);
  return (
    <div className={`imv-resumo${temCredito ? ' cinco' : ''}`}>
      <div className="imv-kpi k-livre">
        <span className="imv-rot">Ações cotadas</span>
        <strong>{somaEmNome === null ? '—' : dinheiro(somaEmNome)}</strong>
        <small>{plural(emNome.length, 'posição', 'posições')} · em linha própria</small>
      </div>
      <div className="imv-kpi k-onus">
        <span className="imv-rot">Ações oneradas</span>
        <strong>{somaOneradas === null ? '—' : dinheiro(somaOneradas)}</strong>
        <small>{plural(oneradas.length, 'posição', 'posições')} · com o onerado</small>
      </div>
      {temCredito && (
        <div className="imv-kpi k-livre">
          <span className="imv-rot">Créditos livres</span>
          <strong>{somaCreditos === null ? '—' : dinheiro(somaCreditos)}</strong>
          <small>{plural(creditosLivres.length, 'crédito', 'créditos')} · soma no livre</small>
        </div>
      )}
      <div className="imv-kpi k-sem">
        <span className="imv-rot">Fora da conta</span>
        <strong>{porRegra}</strong>
        <small>marca, patente, mineração, exterior{temCredito ? ', crédito penhorado' : ''} — da parte</small>
      </div>
      <div className="imv-kpi k-fora">
        <span className="imv-rot">Fora do atual</span>
        <strong>{saiu}</strong>
        <small>saiu ou deixou de constar</small>
      </div>
    </div>
  );
}

/* ── A lista ──────────────────────────────────────────────────────────────── */
function ListaDosOutros({
  dados,
  investigadoId,
  ehMaster,
  aoMudar,
  aoCorrigir,
}: {
  dados: Dados | undefined;
  investigadoId: string;
  ehMaster: boolean;
  aoMudar: () => void;
  aoCorrigir: (a: AtivoDoInvestigado) => void;
}) {
  const [aberto, setAberto] = useState<string | null>(null);
  if (!dados) return <p className="ajuda">Carregando…</p>;
  const g = dados.gravames;
  const ordemDe = (a: AtivoDoInvestigado) => (a.outro ? SITUACOES_DO_OUTRO[a.outro].findIndex((s) => s.chave === a.classificacao) : 9);
  const ordem = [...dados.ativos].sort(
    (x, y) =>
      OUTROS.findIndex((o) => o.chave === x.outro) - OUTROS.findIndex((o) => o.chave === y.outro) || ordemDe(x) - ordemDe(y),
  );
  return (
    <>
      <div className="imv-lista">
        <div className="imv-grade imv-cab">
          <span className="imv-rot">Situação</span>
          <span className="imv-rot">Bem</span>
          <span className="imv-rot">Posição ou registro</span>
          <span className="imv-rot dir">Valor</span>
          <span />
        </div>
        {ordem.length === 0 ? (
          /* ⚠️ Vazio diz "nada lançado" e NÃO some: ausência de bem não é ausência de pesquisa. */
          <p className="imv-vazio">Nada lançado para esta parte.</p>
        ) : (
          ordem.map((a) => (
            <LinhaDoOutro
              key={a.id}
              ativo={a}
              aberto={aberto === a.id}
              aoAbrir={() => setAberto(aberto === a.id ? null : a.id)}
              investigadoId={investigadoId}
              ehMaster={ehMaster}
              aoMudar={aoMudar}
              aoCorrigir={aoCorrigir}
            />
          ))
        )}
      </div>
      <div className="imv-legenda">
        <span>
          <b>—</b> valor não apurado: o bem conta na lista e fica fora da soma
        </span>
        <span className="imv-legenda-longa">
          <span className="imv-info">i</span> Somam a ação cotada — em linha própria no índice de cobertura — e o direito
          creditório livre com valor; marca, patente, direito minerário e crédito penhorado mostram o valor documentado e{' '}
          <strong>ficam fora da conta</strong>
        </span>
        {g && g.exposicaoQuantos > 0 && (
          <span>
            <span className="imv-info">i</span> Gravames ativos: {g.exposicaoQuantos}
            {g.exposicao > 0 && <> · somam {dinheiro(g.exposicao)}</>}
            {g.exposicaoSemValor > 0 && <> · {g.exposicaoSemValor} sem valor no papel</>} — valor do ato, não saldo
            devedor; <strong>não se subtraem</strong> do patrimônio
          </span>
        )}
        {g && g.gravamesSemSituacao > 0 && (
          <span>
            ⚠️ {g.gravamesSemSituacao} gravame(s) com <strong>situação não apurada</strong> — não somam e não se ignoram
          </span>
        )}
      </div>
    </>
  );
}

function LinhaDoOutro({
  ativo,
  aberto,
  aoAbrir,
  investigadoId,
  ehMaster,
  aoMudar,
  aoCorrigir,
}: {
  ativo: AtivoDoInvestigado;
  aberto: boolean;
  aoAbrir: () => void;
  investigadoId: string;
  ehMaster: boolean;
  aoMudar: () => void;
  aoCorrigir: (a: AtivoDoInvestigado) => void;
}) {
  const [aApagar, setAApagar] = useState(false);
  /* ⛔ Apagar um gravame pergunta antes — a regra dele, 18/09/2026. */
  const [gravameAApagar, setGravameAApagar] = useState<AtivoDoInvestigado['onus'][number] | null>(null);
  const gravamesAtivos = ativo.onus.filter(ativoGravame);
  const sit = situacao(ativo);
  const ehAcao = ativo.outro === 'acao';
  const deixou = ativo.classificacao === 'acao_deixou_de_constar';
  const saiu = ['marca_transferida', 'patente_transferida', 'mineral_cedido', 'credito_cedido'].includes(ativo.classificacao);
  const capital = ativo.percentualDoCapital !== null ? `${String(ativo.percentualDoCapital).replace('.', ',')}% do capital` : null;

  /* A terceira coluna: na ação, a posição; no resto, a data do registro ou da saída. */
  const posicao = ehAcao
    ? {
        titulo: ativo.quantidade !== null ? `${ativo.quantidade.toLocaleString('pt-BR')} ações` : 'quantidade não apurada',
        linha: [ativo.certidaoEm ? `${deixou ? 'constava' : 'consta'} em ${emData(ativo.certidaoEm)}` : null, capital].filter(Boolean).join(' · '),
      }
    : {
        titulo: ativo.ocorridoEm ? (saiu ? 'Saída' : /extint/.test(ativo.classificacao) ? 'Extinção' : ativo.outro === 'direito_creditorio' ? 'Origem' : 'Registro') : '—',
        linha: [
          emData(ativo.ocorridoEm) || null,
          ativo.prazoDeValidade ? `vigência ${ativo.prazoDeValidade}` : null,
          saiu && ativo.adquirente ? `para ${ativo.adquirente}${ativo.vinculoEntrePartes ? ` (${ativo.vinculoEntrePartes.toLocaleLowerCase('pt-BR')})` : ''}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
      };

  return (
    <article className={`imv-linha${aberto ? ' aberta' : ''}`}>
      <button type="button" className="imv-grade imv-linha-topo" onClick={aoAbrir} aria-expanded={aberto}>
        <span className="imv-sit-coluna">
          <span className={`imv-sit ${sit?.tom ?? ''}`}>
            <i />
            {sit?.curto ?? ROTULO[ativo.classificacao]}
          </span>
          {gravamesAtivos.length > 0 && (
            <span className="imv-tag">
              {gravamesAtivos.length} gravame{gravamesAtivos.length === 1 ? '' : 's'} ativo{gravamesAtivos.length === 1 ? '' : 's'}
            </span>
          )}
          {ativo.foraDaContaPorRegra && <span className="imv-tag cinza">fica fora da conta</span>}
          {!ativo.entraNaConsolidacao && !ativo.foraDaContaPorRegra && <span className="imv-tag cinza">fora do patrimônio atual</span>}
        </span>
        <span className="imv-bem">
          <b>{ativo.descricao}</b>
          {ativo.identificacao && <span className="imv-mono">{ativo.identificacao}</span>}
          <small>
            {nomeDe(ativo.outro)}
            {/* ⭐ 07/10/2026: no direito creditório, quem deve à parte. */}
            {ativo.outro === 'direito_creditorio' && ativo.outraParte ? ` · devedor: ${ativo.outraParte}` : ''}
          </small>
        </span>
        <span className="imv-ato">
          <b>{posicao.titulo}</b>
          {posicao.linha && <small>{posicao.linha}</small>}
        </span>
        <span className="imv-valor">
          {ativo.valorAtribuivel !== null ? (
            <>
              <b>{dinheiro(ativo.valorAtribuivel)}</b>
              <small>
                {ehAcao && ativo.cotacao !== null
                  ? `cotação ${dinheiro(ativo.cotacao)}${ativo.cotacaoEm ? ` em ${emData(ativo.cotacaoEm)}` : ''}`
                  : ativo.modalidadeDaAvaliacao
                    ? ROTULO_DA_MODALIDADE[ativo.modalidadeDaAvaliacao].toLocaleLowerCase('pt-BR')
                    : 'procedência não declarada'}
                {!ativo.entraNaConsolidacao ? ' · não soma' : ''}
              </small>
            </>
          ) : (
            <>
              <b className="na">—</b>
              <small>não apurado</small>
            </>
          )}
        </span>
        <span className="imv-chev" aria-hidden="true">
          <Traco d={[aberto ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6']} />
        </span>
      </button>

      {aberto && (
        <div className="imv-detalhe">
          {ehAcao ? (
            <>
              <div>
                <h5 className="imv-rot">A posição</h5>
                <ul className="imv-mini">
                  <li>
                    <span>{ativo.quantidade !== null ? `${ativo.quantidade.toLocaleString('pt-BR')} ações` : 'Quantidade não apurada'}{capital ? ` · ${capital}` : ''}</span>
                  </li>
                  <li>
                    <span>{ativo.certidaoEm ? `${deixou ? 'Constava' : 'Consta'} em ${emData(ativo.certidaoEm)}, no documento da companhia` : 'Data da posição não apurada'}</span>
                  </li>
                  {ativo.situacao && (
                    <li>
                      <span>Companhia: {ativo.situacao}</span>
                    </li>
                  )}
                </ul>
                {deixou && <p className="imv-nada">Deixou de constar não afirma venda: a posição pode ter caído abaixo de 5%.</p>}
              </div>
              <div>
                <h5 className="imv-rot">A cotação</h5>
                <ul className="imv-mini">
                  <li className={ativo.avaliacao !== null && !deixou ? 'conta' : undefined}>
                    <b>{ativo.avaliacao !== null ? dinheiro(ativo.avaliacao) : '—'}</b>
                    <span>
                      {ativo.cotacao !== null
                        ? `${ativo.quantidade?.toLocaleString('pt-BR') ?? '—'} × ${dinheiro(ativo.cotacao)}${ativo.cotacaoEm ? ` · fechamento em ${emData(ativo.cotacaoEm)}` : ''}`
                        : 'cotação não apurada — o valor fica em branco, nunca zero'}
                    </span>
                    {ativo.avaliacao !== null && !deixou && <em>na conta</em>}
                  </li>
                </ul>
                <p className="imv-nada">A conta é do Hub: quantidade × cotação oficial de fechamento.</p>
              </div>
            </>
          ) : (
            <>
              <div>
                <h5 className="imv-rot">Avaliações</h5>
                {(ativo.avaliacoes ?? []).length === 0 ? (
                  <p className="imv-nada">Sem valor documentado. Não é zero.</p>
                ) : (
                  <ul className="imv-mini">
                    {ativo.avaliacoes.map((x) => (
                      <li key={x.id}>
                        <b>{dinheiro(x.valor)}</b>
                        <span>
                          {x.modalidade ? ROTULO_DA_MODALIDADE[x.modalidade] : 'procedência não declarada'}
                          {x.modalidade && !DOCUMENTADA[x.modalidade] && x.modalidade !== 'a_apurar' ? ' (derivada)' : ''}
                          {x.referencia ? ` · ${x.referencia}` : ''}
                          {x.avaliadaEm ? ` · ${emData(x.avaliadaEm)}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {ativo.foraDaContaPorRegra && <p className="imv-nada">Fica fora da conta: sem cotação, o valor depende de laudo.</p>}
              </div>
              <div>
                <h5 className="imv-rot">Registro</h5>
                <ul className="imv-mini">
                  {ativo.identificacao && (
                    <li>
                      <span>{ativo.identificacao}</span>
                    </li>
                  )}
                  {ativo.percentual !== null && (
                    <li>
                      <span>Participação {ativo.percentual}%</span>
                    </li>
                  )}
                  {ativo.prazoDeValidade && (
                    <li>
                      <span>Vigência: {ativo.prazoDeValidade}</span>
                    </li>
                  )}
                  <li>
                    <span>{ativo.certidaoEm ? `Situação consultada em ${emData(ativo.certidaoEm)}` : 'Data da consulta não apurada'}</span>
                  </li>
                  {ativo.adquirente && (
                    <li>
                      <span>
                        Para {ativo.adquirente}
                        {ativo.vinculoEntrePartes ? ` (${ativo.vinculoEntrePartes})` : ''}
                      </span>
                    </li>
                  )}
                  {ativo.proprietarioAtual && (
                    <li>
                      <span>Titular atual: {ativo.proprietarioAtual}</span>
                    </li>
                  )}
                </ul>
              </div>
            </>
          )}

          <div>
            <h5 className="imv-rot">Gravames</h5>
            {ativo.onus.length === 0 ? (
              <p className="imv-nada">Nenhum lançado.</p>
            ) : (
              <ul className="imv-mini">
                {ativo.onus.map((o) => (
                  <li key={o.id}>
                    <b>{o.valor !== null ? dinheiro(o.valor) : '—'}</b>
                    <span>
                      {o.natureza}
                      {o.credor ? ` · ${o.credor}` : ''}
                      {o.constituidoEm ? ` · desde ${emData(o.constituidoEm)}` : ''}
                    </span>
                    <em className={o.situacao ? 'sit' : 'sit na'}>{o.situacao ?? 'não apurada'}</em>
                    {ehMaster && (
                      <button type="button" className="botao-texto" onClick={() => setGravameAApagar(o)}>
                        apagar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {ehMaster && <FormularioDeOnus ativo={ativo} investigadoId={investigadoId} aoLancar={aoMudar} />}
          </div>

          {ehAcao && (
            <div className="imv-travas">
              <p>
                ⚠️ <strong>A posição declarada não afirma a inexistência de ônus ou constrição:</strong> penhor e bloqueio
                de ação ficam na B3, fora da vista de terceiros.
              </p>
            </div>
          )}

          {ativo.interpretacao && (
            <div className={`imv-interpretacao${ativo.aceita ? ' aceita' : ''}`}>
              <p>{ativo.interpretacao}</p>
              <p className="ajuda">
                {ativo.aceita ? 'Aceita — entra no documento entregue.' : 'Escrita pelo Hub, ainda não revista. Não entra no documento entregue.'}
                {ehMaster && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="botao-texto"
                      onClick={() =>
                        void (ativo.aceita
                          ? apiInvestigados.desfazerAceite(investigadoId, ativo.id)
                          : apiInvestigados.aceitarAtivo(investigadoId, ativo.id)
                        )
                          .then(() => {
                            aoMudar();
                            avisar(ativo.aceita ? 'Aceite desfeito.' : 'Aceita — entra no documento entregue.');
                          })
                          .catch(avisarErro)
                      }
                    >
                      {ativo.aceita ? 'desfazer o aceite' : 'aceitar'}
                    </button>
                  </>
                )}
              </p>
            </div>
          )}

          {ehMaster && (
            <div className="imv-detalhe-acoes">
              <button type="button" className="imv-bt p" onClick={() => aoCorrigir(ativo)}>
                <Traco d={['M4 20h4L19 9l-4-4L4 16z', 'M13.5 6.5l4 4']} />
                Corrigir
              </button>
              <button type="button" className="imv-bt p fantasma perigo" onClick={() => setAApagar(true)}>
                <Traco d={['M4 7h16', 'M9 7V4.5h6V7', 'M6.5 7l1 13h9l1-13']} />
                Apagar
              </button>
            </div>
          )}
        </div>
      )}

      {aApagar && (
        <Confirmar
          titulo="Apagar este lançamento?"
          descricao={
            ativo.origem
              ? `"${ativo.descricao}" veio de consulta. Apagá-lo não devolve o que a consulta custou.`
              : `"${ativo.descricao}" será apagado, com os gravames lançados nele.`
          }
          aoConfirmar={async () => {
            await apiInvestigados.apagarAtivo(investigadoId, ativo.id);
            setAApagar(false);
            aoMudar();
          }}
          aoFechar={() => setAApagar(false)}
        />
      )}
      {gravameAApagar && (
        <Confirmar
          titulo="Apagar este gravame?"
          descricao={`${gravameAApagar.natureza}${gravameAApagar.credor ? ` · ${gravameAApagar.credor}` : ''} sai deste bem agora, e não volta.`}
          aoConfirmar={() =>
            void apiInvestigados
              .apagarOnus(investigadoId, ativo.id, gravameAApagar.id)
              .then(aoMudar)
              .catch(avisarErro)
          }
          aoFechar={() => setGravameAApagar(null)}
        />
      )}
    </article>
  );
}
